import React from 'react'
import { Avatar, Box, Popover, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { signOut } from 'next-auth/react'

import styles from '#/app/layout/styles/styles.module.css'

import infoBarStyles from './info-bar.module.scss'
import { useAppSelector } from '#/app/store/store'
import { TooltipCustom } from '#/shared'
import { getDaysLeft } from '#/shared/lib/helpers/date-helper'
import { declineToken } from '#/shared/lib/helpers/get-token'
import { useFeatureFlag } from '#/shared/lib/hooks'
import { IProps } from '#/shared/lib/types/entities'
import { ChatSearch } from '#/features/chat-search'
import { SubscriptionDaysBadge } from '#/shared/ui/subscription-days-badge/subscription-days-badge'
import { SvgIcon } from '#/shared/ui/svg'

interface InfoBarProps extends IProps {}

type LangFull = 'Русский' | 'English'

type LangShort = 'ru' | 'en'

const languages: Record<LangFull, LangShort> = {
	Русский: 'ru',
	English: 'en',
}

type LangParam<T extends boolean> = T extends true ? LangFull : LangShort

type LangReturn<T extends boolean> = T extends true ? LangShort : LangFull

const convertLang = <T extends boolean>(toShort: T, lang: LangParam<T>): LangReturn<T> => {
	const arrLang = Object.entries(languages) as [LangFull, LangShort][]
	if (toShort) {
		return arrLang.find(([key]) => lang === key)![1] as LangReturn<T>
	}
	return arrLang.find(([_, value]) => lang === value)![0] as LangReturn<T>
}

const RECURRENT_PAYMENTS_FLAG = 'recurrent-payments-enabled'

const InfoBar: React.FC<InfoBarProps> = ({ device }) => {
	const recurrentPaymentsEnabled = useFeatureFlag(RECURRENT_PAYMENTS_FLAG)
	const nextPaymentAt = useAppSelector((state) => state.user.payment_plan.next_payment_at)
	const daysLeft = getDaysLeft(nextPaymentAt)
	const balance = useAppSelector((state) => state.balance.balance)
	const { email, first_name, last_name, profile_picture_link, account_type, show_balance } = useAppSelector((state) => state.user)

	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
	const [anchorEl2, setAnchorEl2] = React.useState<HTMLButtonElement | null>(null)

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClick2 = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl2(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleClose2 = () => {
		setAnchorEl2(null)
	}

	const open = Boolean(anchorEl)
	const open2 = Boolean(anchorEl2)
	const id = open ? 'simple-popover' : undefined
	const router = useRouter()

	return (
		<Box className={styles.infoHeader}>
			<Box display='flex' alignItems='center' width='100%' gap='24px'>
				<ChatSearch variant='desktop' />
				<Stack
					direction='row'
					display='flex'
					alignItems='center'
					justifyContent='center'
					className='tutorial-balance'
					sx={{
						marginLeft: 'auto',
						marginRight: '80px',
						flexShrink: 0,
					}}
				>
					{recurrentPaymentsEnabled && daysLeft != null ? <SubscriptionDaysBadge days={daysLeft} isInfoBar /> : null}
					<Popover
						sx={{ marginTop: '6px' }}
						PaperProps={{
							style: {
								backgroundColor: '#151518',
								borderRadius: '13px',
								boxShadow: 'none',
							},
						}}
						id={id}
						open={open2}
						anchorEl={anchorEl2}
						onClose={handleClose2}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'left',
						}}
					>
						<Box padding={2} width={300} textAlign='center'>
							<Typography fontSize={16} marginBottom={1}>
								Мы уже работаем над этой проблемой. Попробуйте перезайти в аккаунт
							</Typography>
							<Button fullWidth className='btn-classic' onClick={() => signOut()}>
								Перезайти
							</Button>
						</Box>
					</Popover>

					<Box display='flex'>
						<Box className={styles.infoContainer} sx={{ marginLeft: '30px' }}>
							<Box>
								<Typography className={styles.name} sx={{ fontSize: 15 }}>
									{first_name + ' ' + last_name}
								</Typography>
							</Box>
							{show_balance && (
								<Box className={styles.balanceBox}>
									<SvgIcon
										className={styles.generationIcon}
										url='/svg/chatgpt/generation-icon.svg'
										width={12}
										height={16}
									/>
									<Typography className={styles.balance} sx={{ fontSize: 15 }}>
										{declineToken(balance.toString())}
									</Typography>
								</Box>
							)}
						</Box>
						<div>
							<Avatar
								aria-describedby={id}
								onClick={(e: any) => {
									//@ts-ignore
									handleClick(e)
								}}
								src={profile_picture_link as string}
								sx={{
									width: 40,
									height: 40,
									bgcolor: '#8280FF',
									marginLeft: '10px',
									cursor: 'pointer',
								}}
							>
								{email ? email[0] : 'N'}
							</Avatar>
							<Popover
								sx={{ marginTop: '6px' }}
								PaperProps={{
									style: {
										backgroundColor: '#151518',
										borderRadius: '13px',
										boxShadow: 'none',
									},
								}}
								id={id}
								open={open}
								anchorEl={anchorEl}
								onClose={handleClose}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
							>
								<Box padding={2}>
									<Link href={'/account?scope=setting'}>
										<Box display='flex' onClick={() => router.push('/account?scope=setting')}>
											<Image src={'/svg/setting.svg'} width={19} height={19} alt={''} />
											<Typography className='title-main-gray' sx={{ marginLeft: '10px' }}>
												{' '}
												Настройки{' '}
											</Typography>
										</Box>
									</Link>
									<Box className={infoBarStyles.sectionSpacing}>
										<Link href={'/account?scope=business'}>
											<Box display='flex' onClick={() => router.push('/account?scope=business')}>
												<Image src={'/svg/side-menu/corp-acc-off.svg'} width={18} height={18} alt={''} />
												<Typography className='title-main-gray' sx={{ marginLeft: '10px' }}>
													{' '}
													Компаниям{' '}
												</Typography>
											</Box>
										</Link>
									</Box>

									{account_type === 'regular' && (
										<Box className={infoBarStyles.sectionSpacing}>
											<Link href={'/account?scope=referral'}>
												<Box display='flex' onClick={() => router.push('/account?scope=referral')}>
													<Image src={'/svg/side-menu/referral.svg'} width={18} height={18} alt={''} />
													<Typography className='title-main-gray' sx={{ marginLeft: '10px' }}>
														{' '}
														Рефералам{' '}
													</Typography>
												</Box>
											</Link>
										</Box>
									)}
									<Box
										display='flex'
										onClick={() => {
											signOut()
										}}
										sx={{ marginTop: '15px', cursor: 'pointer' }}
									>
										<Image src={'/logout-off.svg'} width={18} height={18} alt={''} />
										<Typography sx={{ marginLeft: '10px' }} className='title-main-gray'>
											Выйти
										</Typography>
									</Box>
								</Box>
							</Popover>
						</div>
					</Box>
				</Stack>
			</Box>
		</Box>
	)
}

export default InfoBar
